import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getApiBaseUrl, publicMediaSrc } from "../config/api";
import { productHref } from "../components/CategoryCard";
import { deliveryHref } from "../components/deliveryHref";
import ProductSeo, { productCanonicalPath } from "../components/ProductSeo";
import { trackPublicPageview } from "../utils/trackPageview";
import { WHATSAPP_ICON_PATH, whatsappEnquiryHref } from "../utils/whatsappEnquiry";
import NotFoundPage from "./NotFoundPage";

const PAGE_GUTTER = "px-16 md:px-32 lg:px-40";

function lockDoubleClick(event) {
  const el = event.currentTarget;
  if (el.dataset.navLock === "1") {
    event.preventDefault();
    return;
  }
  el.dataset.navLock = "1";
  window.setTimeout(() => {
    delete el.dataset.navLock;
  }, 700);
}

function cleanText(value) {
  const text = String(value || "").trim();
  if (!text || text === "undefined" || text === "null") return "";
  return text;
}

function categoryIdOf(item) {
  return String(item?.category?._id || item?.category || "");
}

function categorySlugOf(item) {
  return cleanText(item?.category?.slug);
}

function categoryNameOf(item) {
  return cleanText(item?.category?.categoryName);
}

function formatDeliveryDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function deliveryCategoryIdOf(row) {
  return String(row?.category?._id || row?.category || "");
}

function deliveryHasCategory(item, categoryId) {
  if (!categoryId) return false;
  const list = Array.isArray(item?.product) ? item.product : [];
  return list.some((row) => deliveryCategoryIdOf(row) === categoryId);
}

function FeatureIcon({ name }) {
  const common = {
    className: "h-6 w-6 text-at-gold",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.6",
    viewBox: "0 0 24 24",
    "aria-hidden": "true",
  };
  if (name === "qty") {
    return (
      <svg {...common}>
        <path d="M12 3l8 4-8 4-8-4 8-4z" />
        <path d="M4 11l8 4 8-4" />
        <path d="M4 15l8 4 8-4" />
      </svg>
    );
  }
  if (name === "truck") {
    return (
      <svg {...common}>
        <path d="M3 7h11v8H3z" />
        <path d="M14 10h4l3 3v2h-7" />
        <circle cx="7" cy="17" r="1.5" />
        <circle cx="18" cy="17" r="1.5" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M12 21s7-5.3 7-11a7 7 0 10-14 0c0 5.7 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.2" />
    </svg>
  );
}

function ProductDetailPage() {
  const { categorySlug, productSlug } = useParams();
  const pageRef = useRef(null);
  const imageBtnRef = useRef(null);
  const lightboxRef = useRef(null);
  const closeBtnRef = useRef(null);
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [contact, setContact] = useState(null);
  const [status, setStatus] = useState("loading");
  const [loadNonce, setLoadNonce] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    setProduct(null);
    setRelated([]);
    setDeliveries([]);
    setStatus("loading");
    setLightboxOpen(false);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [categorySlug, productSlug]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (!cancelled) setContact(Array.isArray(list) && list[0] ? list[0] : null);
      })
      .catch(() => {
        if (!cancelled) setContact(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setDeliveries([]);

    fetch(`${getApiBaseUrl()}/products`, { headers: { Accept: "application/json" } })
      .then((res) => {
        if (!res.ok) throw new Error("Unable to load products");
        return res.json();
      })
      .then((productList) => {
        if (cancelled) return null;
        const published = (Array.isArray(productList) ? productList : []).filter(
          (item) => item?.isPublished !== false
        );
        const match = published.find(
          (item) => item?.slug === productSlug && categorySlugOf(item) === categorySlug
        );
        if (!match) {
          setProduct(null);
          setRelated([]);
          setStatus("missing");
          return null;
        }
        const categoryId = categoryIdOf(match);
        const seen = new Set();
        const relatedProducts = published.filter((item) => {
          const id = String(item._id || item.slug || "");
          if (!id || id === String(match._id) || categoryIdOf(item) !== categoryId || seen.has(id)) return false;
          seen.add(id);
          return true;
        }).slice(0, 4);
        setProduct(match);
        setRelated(relatedProducts);
        setStatus("ready");
        return categoryId;
      })
      .then((categoryId) => {
        if (!categoryId || cancelled) return;
        return fetch(`${getApiBaseUrl()}/deliveries`, { headers: { Accept: "application/json" } })
          .then((res) => {
            if (!res.ok) throw new Error("Unable to load deliveries");
            return res.json();
          })
          .then((deliveryList) => {
            if (cancelled) return;
            const relatedDeliveries = (Array.isArray(deliveryList) ? deliveryList : [])
              .filter((item) => item?.isPublished !== false)
              .filter((item) => deliveryHasCategory(item, categoryId))
              .filter((item) => Array.isArray(item.images) && item.images[0]?.url)
              .sort((a, b) => new Date(b.deliveryDate || 0) - new Date(a.deliveryDate || 0))
              .slice(0, 3);
            setDeliveries(relatedDeliveries);
          })
          .catch(() => {
            if (!cancelled) setDeliveries([]);
          });
      })
      .catch(() => {
        if (!cancelled) {
          setProduct(null);
          setRelated([]);
          setDeliveries([]);
          setStatus("error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [categorySlug, productSlug, loadNonce]);

  useEffect(() => {
    if (status !== "ready" || !product?.slug) return;
    trackPublicPageview({
      path: `/category/${categorySlug}/${productSlug}`,
      title: cleanText(product.productName),
    });
  }, [status, product, categorySlug, productSlug]);

  const retry = useCallback(() => {
    setLoadNonce((value) => value + 1);
  }, []);

  const name = cleanText(product?.productName);
  const categoryName = categoryNameOf(product);
  const description = cleanText(product?.description);
  const brand = cleanText(product?.brand);
  const sku = cleanText(product?.sku);
  const modelNumber = cleanText(product?.modelNumber);
  const photo = publicMediaSrc(product?.image);
  const siteName = cleanText(contact?.siteName);
  const photoAlt =
    cleanText(product?.imageAlt) || (name && siteName ? `${name} supplied by ${siteName}` : name);
  const productPath = productCanonicalPath(categorySlug, productSlug);
  const waHref = whatsappEnquiryHref({
    number: contact?.whatsappNumber,
    siteName: contact?.siteName,
    title: name,
    path: productPath,
  });
  const specs = useMemo(
    () =>
      [
        brand ? { label: "Brand", value: brand } : null,
        sku ? { label: "SKU", value: sku } : null,
        modelNumber ? { label: "Model Number", value: modelNumber } : null,
      ].filter(Boolean),
    [brand, sku, modelNumber]
  );

  useEffect(() => {
    const root = pageRef.current;
    const nodes = root ? root.querySelectorAll("[data-reveal]") : [];
    if (!nodes.length) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      nodes.forEach((node) => node.classList.add("is-visible"));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.16, rootMargin: "0px 0px -6% 0px" }
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [related.length, deliveries.length, status]);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();

    function onKey(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        setLightboxOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const root = lightboxRef.current;
      if (!root) return;
      const focusable = [...root.querySelectorAll("button")];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKey);
    const imageButton = imageBtnRef.current;
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
      imageButton?.focus();
    };
  }, [lightboxOpen]);

  if (status === "missing") {
    return <NotFoundPage />;
  }

  return (
    <div className="product-detail-page" ref={pageRef}>
      <ProductSeo contact={contact} product={product} categorySlug={categorySlug} status={status} />
      <div className="sr-only" aria-live="polite">
        {status === "loading" ? "Loading product" : status === "error" ? "Unable to load product" : name}
      </div>

      <section className={`bg-white py-10 md:py-14 ${PAGE_GUTTER}`}>
        <nav
          key={`${productSlug}-crumb`}
          aria-label="Breadcrumb"
          className="product-in product-in-1 text-xs text-neutral-400"
        >
          <Link to="/" className="hover:text-at-dark">
            Home
          </Link>
          <span className="px-2 text-neutral-300">/</span>
          <Link to="/category" className="hover:text-at-dark">
            Product Categories
          </Link>
          {categoryName ? (
            <>
              <span className="px-2 text-neutral-300">/</span>
              <Link to={`/category/${categorySlug}`} className="hover:text-at-dark">
                {categoryName}
              </Link>
            </>
          ) : null}
          {name ? (
            <>
              <span className="px-2 text-neutral-300">/</span>
              <span className="text-at-dark">{name}</span>
            </>
          ) : null}
        </nav>

        {status === "error" ? (
          <div className="mt-10 max-w-xl">
            <h1 className="font-serif text-3xl text-at-dark">Unable to load product</h1>
            <p className="mt-3 text-sm text-neutral-500">This product could not be loaded. Please try again.</p>
            <button
              type="button"
              onClick={(event) => {
                lockDoubleClick(event);
                retry();
              }}
              className="hero-btn mt-6 inline-flex items-center gap-2 rounded-sm bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
            >
              Retry <span className="btn-arrow">→</span>
            </button>
          </div>
        ) : (
          <div className="mt-8 grid items-start gap-10 lg:grid-cols-2 lg:gap-16">
            <div className="product-in product-in-2 aspect-square overflow-hidden bg-neutral-100" key={`${productSlug}-photo`}>
              {status === "loading" ? (
                <div className="h-full w-full bg-neutral-200" aria-hidden="true" />
              ) : photo ? (
                <button
                  type="button"
                  ref={imageBtnRef}
                  className="product-photo h-full w-full"
                  onClick={() => setLightboxOpen(true)}
                  aria-label={`View larger image of ${photoAlt || name}`}
                >
                  <img
                    src={photo}
                    alt={photoAlt}
                    width={900}
                    height={900}
                    className="h-full w-full object-cover"
                  />
                </button>
              ) : null}
            </div>

            <div className="product-in product-in-3" key={`${productSlug}-copy`}>
              {status === "loading" ? (
                <div aria-hidden="true">
                  <div className="h-3 w-28 bg-neutral-200" />
                  <div className="mt-3 h-10 w-3/4 bg-neutral-200" />
                  <div className="mt-5 h-4 w-full bg-neutral-100" />
                  <div className="mt-2 h-4 w-5/6 bg-neutral-100" />
                </div>
              ) : (
                <>
                  {categoryName ? (
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-at-gold">
                      <Link to={`/category/${categorySlug}`}>{categoryName}</Link>
                    </p>
                  ) : null}
                  <h1 className="mt-2 font-serif text-3xl uppercase leading-tight text-at-dark md:text-5xl">
                    {name}
                  </h1>
                  {specs.length ? (
                    <dl className="product-in product-in-3 mt-5 space-y-1 text-sm text-at-dark">
                      {specs.map((item) => (
                        <div key={item.label}>
                          <dt className="inline font-bold">{item.label}</dt>
                          <dd className="inline">: {item.value}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                  {description ? (
                    <p className={`max-w-xl whitespace-pre-line text-sm leading-relaxed text-neutral-600 ${specs.length ? "mt-4" : "mt-5"}`}>
                      {description}
                    </p>
                  ) : null}
                  <div className="mt-8 flex flex-wrap gap-3">
                    <Link
                      to="/contact?intent=quote#contact-form"
                      state={{
                        productName: name,
                        slug: productSlug,
                        categorySlug,
                        categoryName,
                      }}
                      onClick={lockDoubleClick}
                      className="hero-btn inline-flex items-center gap-2 rounded-sm bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
                    >
                      <svg viewBox="0 0 24 24" className="btn-icon h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                        <path d="M7 3h10v18H7z" />
                        <path d="M9 7h6M9 11h6M9 15h4" />
                      </svg>
                      Request a Quote
                    </Link>
                    <a
                      href={waHref || "/contact"}
                      target={waHref ? "_blank" : undefined}
                      rel={waHref ? "noopener noreferrer" : undefined}
                      onClick={lockDoubleClick}
                      className="hero-btn inline-flex items-center gap-2 rounded-sm border border-neutral-300 bg-white px-5 py-2.5 text-sm font-semibold text-at-dark hover:border-at-gold"
                    >
                      <svg viewBox="0 0 24 24" className="btn-icon h-4 w-4" fill="currentColor" aria-hidden="true">
                        <path d={WHATSAPP_ICON_PATH} />
                      </svg>
                      WhatsApp Us
                    </a>
                  </div>
                  <ul className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-xs text-neutral-600">
                    <li className="reassure-item flex items-center gap-2">
                      <FeatureIcon name="qty" />
                      Flexible Quantities
                    </li>
                    <li className="reassure-item flex items-center gap-2">
                      <FeatureIcon name="truck" />
                      Fast Sourcing
                    </li>
                    <li className="reassure-item flex items-center gap-2">
                      <FeatureIcon name="pin" />
                      Malaysia Delivery
                    </li>
                  </ul>
                </>
              )}
            </div>
          </div>
        )}
      </section>

      {status === "ready" && related.length ? (
        <section className={`bg-neutral-50 py-14 ${PAGE_GUTTER}`} data-reveal="">
          <h2 className="font-serif text-3xl uppercase text-at-dark">Related Products</h2>
          <div className="mt-8 grid grid-cols-2 gap-6 md:grid-cols-4">
            {related.map((item, index) => {
              const relatedPhoto = publicMediaSrc(item.image);
              return (
                <Link
                  key={item._id || item.slug}
                  to={productHref(item, categorySlug)}
                  className="product-link-card group block"
                  data-reveal=""
                  style={{ "--reveal-i": Math.min(index, 3) }}
                >
                  <span className="block aspect-square overflow-hidden bg-white">
                    {relatedPhoto ? (
                      <img
                        src={relatedPhoto}
                        alt={item.imageAlt || item.productName || ""}
                        width={600}
                        height={600}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    ) : null}
                  </span>
                  <span className="mt-3 flex items-start justify-between gap-2">
                    <span>
                      <h3 className="text-sm font-semibold text-at-dark">{item.productName}</h3>
                      <span className="mt-0.5 block text-xs text-neutral-500">{categoryName}</span>
                    </span>
                    <span className="card-arrow shrink-0 text-lg text-at-gold">→</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      ) : null}

      {status === "ready" && deliveries.length ? (
        <section className={`bg-[#f5f5f5] py-14 ${PAGE_GUTTER}`} data-reveal="">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="font-serif text-3xl uppercase text-at-dark">Recent Related Deliveries</h2>
              <p className="mt-2 text-sm text-neutral-500">See where this product has recently been supplied.</p>
            </div>
            <Link to="/deliveries" className="hidden text-sm text-neutral-500 hover:text-at-dark sm:inline">
              View All Deliveries <span className="btn-arrow">→</span>
            </Link>
          </div>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {deliveries.map((item, index) => {
              const firstImage = item.images[0];
              const deliveryPhoto = publicMediaSrc(firstImage.url);
              return (
                <Link
                  key={item._id || item.slug}
                  to={deliveryHref(item)}
                  className="product-link-card group block"
                  data-reveal=""
                  style={{ "--reveal-i": Math.min(index, 2) }}
                >
                  <span className="block aspect-[4/3] overflow-hidden bg-neutral-100">
                    {deliveryPhoto ? (
                      <img
                        src={deliveryPhoto}
                        alt={firstImage.altText || item.title || ""}
                        width={800}
                        height={600}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    ) : null}
                  </span>
                  <span className="mt-4 flex items-start justify-between gap-3">
                    <span>
                      <h3 className="text-sm font-semibold text-at-dark">{item.title}</h3>
                      <span className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500">
                        {cleanText(item.deliveryLocation) ? (
                          <span className="inline-flex items-center gap-1">
                            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-at-gold" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                              <path d="M12 21s7-5.3 7-11a7 7 0 10-14 0c0 5.7 7 11 7 11z" />
                              <circle cx="12" cy="10" r="2" />
                            </svg>
                            {item.deliveryLocation}
                          </span>
                        ) : null}
                        {formatDeliveryDate(item.deliveryDate) ? (
                          <span className="inline-flex items-center gap-1">
                            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-at-gold" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                              <rect x="4" y="5" width="16" height="15" rx="1.5" />
                              <path d="M8 3v4M16 3v4M4 10h16" />
                            </svg>
                            {formatDeliveryDate(item.deliveryDate)}
                          </span>
                        ) : null}
                      </span>
                    </span>
                    <span className="card-arrow shrink-0 text-lg text-at-gold">→</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      ) : null}

      {lightboxOpen && photo ? (
        <div
          ref={lightboxRef}
          className="product-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={photoAlt || name}
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            ref={closeBtnRef}
            className="product-lightbox-close"
            aria-label="Close image"
            onClick={(event) => {
              event.stopPropagation();
              setLightboxOpen(false);
            }}
          >
            ×
          </button>
          <img
            src={photo}
            alt={photoAlt}
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      ) : null}
    </div>
  );
}

export default ProductDetailPage;
